import { InspectionPartsEstimateConfig } from '../buildInspectionPageEstimateInput';
import { formatInspectionSectionHeading } from './inspectionPdfLabels';
import { PdfBlock, pdfRichText, pdfText } from './pdf/pdfBlocks';
import { PDF_CONTENT_WIDTH } from './pdf/pdfPage';
import { boldFont } from './pdf/pdfTextMetrics';

/** `Color.Gray`: captions printed in front of a rich text value. */
const CAPTION_COLOR = '#979797';

/** `inspectionSectionLabelParams`: the heading of a numbered report section. */
const SECTION_HEADING_FONT = boldFont(12);
const SECTION_HEADING_MARGIN = { top: 16, bottom: 8 };

/** `nestedSectionLabelParams` with the bottom margin the general standard sub-section adds. */
const NESTED_HEADING_FONT = boldFont(10);
const NESTED_HEADING_MARGIN = { top: 8, bottom: 8 };

export function greyHtml(text: string): string {
  return `<span style="color:${CAPTION_COLOR}">${text}</span>`;
}

export function greyCaptionHtml(label: string): string {
  return greyHtml(`${label}:`);
}

/**
 * `PdfHtmlValueFormatter.prefixRichText`: the caption goes inside the first paragraph, so it shares the
 * value's first line instead of taking one of its own.
 */
export function prefixRichTextHtml(html: string, label: string): string {
  const prefix = `${greyCaptionHtml(label)} `;
  const openingBlockTag = html.match(/^\s*<(p|div|h[1-6])\b[^>]*>/i);
  return openingBlockTag
    ? html.replace(openingBlockTag[0], `${openingBlockTag[0]}${prefix}`)
    : `${prefix}${html}`;
}

export function prefixedRichText(
  html: string,
  label: string,
  width: number,
  marginTop = 0
): PdfBlock {
  return pdfRichText(prefixRichTextHtml(html, label), width, {
    margin: { top: marginTop },
  });
}

export function sectionHeading(label: string, sectionNumber?: number): PdfBlock {
  const text =
    sectionNumber === undefined
      ? label
      : formatInspectionSectionHeading(sectionNumber, label);
  return pdfText(text, PDF_CONTENT_WIDTH, {
    font: SECTION_HEADING_FONT,
    margin: SECTION_HEADING_MARGIN,
  });
}

export function nestedHeading(label: string): PdfBlock {
  return pdfText(label, PDF_CONTENT_WIDTH, {
    font: NESTED_HEADING_FONT,
    margin: NESTED_HEADING_MARGIN,
  });
}

export type InspectionSectionNumbers = Partial<
  Record<keyof Omit<InspectionPartsEstimateConfig, 'signatures'>, number>
>;

/** `numberInspectionSections`: enabled parts are numbered in order; signatures carry no heading. */
export function numberInspectionSections(
  parts: InspectionPartsEstimateConfig
): InspectionSectionNumbers {
  const numbered = (['inspectionInfo', 'summary', 'checklist', 'issueRegister'] as const).filter(
    (part) => parts[part]
  );
  return Object.fromEntries(numbered.map((part, index) => [part, index + 1]));
}
